import { trackRankingTabChanged } from '@/utils/analytics';
import RankingCountdown from "@/components/ranking/RankingCountdown";
import RankingListItem from "@/components/ranking/RankingListItem";
import RankingPodium, { PodiumUser } from "@/components/ranking/RankingPodium";
import { RankingUser, useRanking } from "@/hooks/useRanking";
import { useProfile } from "@/contexts/ProfileContext";
import { useUserScores } from "@/hooks/useUserScores";
import { isChallengeStarted, msUntilChallengeStart } from "@/constants/challenge";
import { formatDisplayName } from "@/utils/name";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect, useRouter } from "expo-router";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActivityIndicator,
    ScrollView,
    StatusBar,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

type ViewType = "enterprise" | "global";


export default function ClassementScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { globalRanking, companyRanking, myRanking, hasCompany, loading, refetch } = useRanking();
  const { profile } = useProfile();
  const { scores } = useUserScores();
  const [viewType, setViewType] = useState<ViewType>("enterprise");
  const [remainingMs, setRemainingMs] = useState(() => msUntilChallengeStart());

  useEffect(() => {
    if (isChallengeStarted()) return;
    const id = setInterval(() => {
      const next = msUntilChallengeStart();
      setRemainingMs(next);
      if (next <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Refetch when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      if (!isChallengeStarted()) return;
      refetch();
    }, [refetch])
  );

  const getRanking = (): RankingUser[] => {
    return viewType === "enterprise" ? companyRanking : globalRanking;
  };
  
  const currentRanking = getRanking();
  
  // Separate podium (top 3) from rest
  const podiumUsers = currentRanking.slice(0, 3);
  const rankedUsers = currentRanking.slice(3);

  // Format podium for component (needs exactly 3)
  const getPodiumData = (): [PodiumUser, PodiumUser, PodiumUser] | null => {
    if (podiumUsers.length < 3) return null;
    return [
      { ...podiumUsers[0], medal: 'gold' as const },
      { ...podiumUsers[1], medal: 'silver' as const },
      { ...podiumUsers[2], medal: 'bronze' as const },
    ];
  };

  const podiumData = getPodiumData();
  // À moins de 3 participants, il n'y a pas de podium → on liste TOUT le monde
  // (sinon l'écran restait vide avec 1-2 collègues). Avec podium, la liste
  // commence au rang 4.
  const listUsers = podiumData ? rankedUsers : currentRanking;
  const listRankOffset = podiumData ? 4 : 1;

  const shouldShowRanking =
    (viewType === "enterprise" && currentRanking.length > 0) ||
    (viewType === "global" && currentRanking.length >= 20);

  if (remainingMs > 0) {
    return (
      <SafeAreaView style={styles.container} edges={["top"]}>
        <StatusBar barStyle="dark-content" backgroundColor="#FBEFE3" />
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header}>
            <Text style={styles.title}>{t("ranking.headerTitle")}</Text>
          </View>
          <RankingCountdown remainingMs={remainingMs} />
        </ScrollView>
      </SafeAreaView>
    );
  }

  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={["top"]}>
        <StatusBar barStyle="dark-content" backgroundColor="#FBEFE3" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#CD7728" />
          <Text style={styles.loadingText}>{t("ranking.loading")}</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={["top"]}>
      <StatusBar barStyle="dark-content" backgroundColor="#FBEFE3" />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Text style={styles.title}>{t("ranking.headerTitle")}</Text>
        </View>

        {/* Tabs */}
        <View style={styles.tabsContainer}>
          <TouchableOpacity
            style={styles.tab}
            onPress={() => { setViewType("enterprise"); trackRankingTabChanged("enterprise"); }}
          >
            {viewType === "enterprise" ? (
              <LinearGradient
                colors={["#CD7728", "#FCC189"]}
                locations={[0, 0.41]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.tabActiveGradient}
              >
                <Text style={[styles.tabText, styles.tabTextActive]}>
                  {t("ranking.tabCompany")}
                </Text>
              </LinearGradient>
            ) : (
              <Text style={styles.tabText}>{t("ranking.tabCompany")}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.tab}
            onPress={() => { setViewType("global"); trackRankingTabChanged("global"); }}
          >
            {viewType === "global" ? (
              <LinearGradient
                colors={["#FCC189", "#CD7728"]}
                locations={[0, 0.41]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.tabActiveGradient}
              >
                <Text style={[styles.tabText, styles.tabTextActive]}>
                  {t("ranking.tabGlobal")}
                </Text>
              </LinearGradient>
            ) : (
              <Text style={styles.tabText}>{t("ranking.tabGlobal")}</Text>
            )}
          </TouchableOpacity>
        </View>
        {/* Company Name Header - Enterprise view with company */}
        {viewType === "enterprise" && hasCompany && profile?.company && (
          <View style={styles.companyHeader}>
            <Ionicons name="business" size={18} color="#1C453F" />
            <Text style={styles.companyHeaderText}>{profile.company}</Text>
          </View>
        )}

        {/* My Position Card - Show for global, or enterprise if has company */}
        {(viewType === "global" || (viewType === "enterprise" && hasCompany)) && (
          <View style={styles.myPositionCard}>
            <LinearGradient
              colors={["#1C453F", "#78A584"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.myPositionGradient}
            >
              <View style={styles.myPositionContent}>
                <View style={styles.myPositionLeft}>
                  <Text style={styles.myPositionLabel}>{t("ranking.myPositionLabel")}</Text>
                  <Text style={styles.myPositionName}>
                    {formatDisplayName(profile?.firstName, profile?.lastName)}
                  </Text>
                </View>
                <View style={styles.myPositionRight}>
                  <Text style={styles.myPositionRank}>
                    #{viewType === "enterprise" 
                      ? (myRanking?.companyRank || "-") 
                      : (myRanking?.globalRank || "-")}
                  </Text>
                  <Text style={styles.myPositionPoints}>
                    {t("common.pointsShort", { points: myRanking?.totalPoints || scores?.totalPoints || 0 })}
                  </Text>
                </View>
              </View>
            </LinearGradient>
          </View>
        )}

        {/* Enterprise view - No company message */}
        {viewType === "enterprise" && !hasCompany && (
          <View style={styles.noCompanyContainer}>
            <Ionicons name="business-outline" size={48} color="#CD7728" />
            <Text style={styles.noCompanyTitle}>{t("ranking.noCompanyTitle")}</Text>
            <Text style={styles.noCompanyText}>
              {t("ranking.noCompanyText")}
            </Text>
            <TouchableOpacity
              style={styles.selectCompanyButton}
              onPress={() => router.push("/edit-profile")}
            >
              <LinearGradient
                colors={["#1C453F", "#78A584"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.selectCompanyGradient}
              >
                <Text style={styles.selectCompanyText}>{t("ranking.chooseCompanyButton")}</Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        )}

        {/* Enterprise view - Has company but empty */}
        {viewType === "enterprise" && hasCompany && currentRanking.length === 0 && (
          <View style={styles.emptyContainer}>
            <Ionicons name="people-outline" size={48} color="#CD7728" />
            <Text style={styles.emptyTitle}>{t("ranking.emptyCompanyTitle")}</Text>
            <Text style={styles.emptyText}>
              {t("ranking.emptyCompanyText")}
            </Text>
          </View>
        )}

        {/* Global view - Not enough users */}
        {viewType === "global" && currentRanking.length < 20 && (
          <View style={styles.emptyContainer}>
            <Ionicons name="globe-outline" size={48} color="#CD7728" />
            <Text style={styles.emptyTitle}>{t("ranking.globalSoonTitle")}</Text>
            <Text style={styles.emptyText}>
              {t("ranking.globalSoonText")}
            </Text>
          </View>
        )}

        {/* Show ranking if we have data */}
        {shouldShowRanking && (
          <>
            {podiumData && (
              <RankingPodium users={podiumData} />
            )}

            {listUsers.length > 0 && (
              <View style={styles.rankedList}>
                {listUsers.map((user, index) => (
                  <RankingListItem
                    key={user.id}
                    user={{ ...user, rank: user.rank ?? (index + listRankOffset) }}
                  />
                ))}
              </View>
            )}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FBEFE3",
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingTop: 0,
    paddingBottom: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
  },
  loadingText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
  },
  tabsContainer: {
    flexDirection: "row",
    backgroundColor: "#FFF",
    borderRadius: 12,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  tab: {
    flex: 1,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    padding: 6,
  },
  tabActiveGradient: {
    flex: 1,
    width: "100%",
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  tabText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
  },
  tabTextActive: {
    padding: 6,
    fontSize: 14,
    color: "#FFF",
  },
  header: {
    marginBottom: 16,
    paddingTop: 24,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 20,
    color: "#000",
    textAlign: "center",
  },
  rankedList: {
    marginTop: 16,
  },
  companyHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 16,
    marginBottom: 12,
  },
  companyHeaderText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 15,
    color: "#1C453F",
  },
  noCompanyContainer: {
    backgroundColor: "#FFF",
    borderRadius: 16,
    padding: 32,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    marginTop: 20,
  },
  noCompanyTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#000",
    marginTop: 16,
    marginBottom: 8,
  },
  noCompanyText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    textAlign: "center",
    lineHeight: 20,
    marginBottom: 20,
  },
  selectCompanyButton: {
    borderRadius: 12,
    overflow: "hidden",
    width: "100%",
  },
  selectCompanyGradient: {
    paddingVertical: 14,
    alignItems: "center",
  },
  selectCompanyText: {
    fontFamily: "Inter_700Bold",
    fontSize: 14,
    color: "#FFF",
  },
  emptyContainer: {
    backgroundColor: "#FFF",
    borderRadius: 16,
    padding: 32,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    marginTop: 20,
  },
  emptyTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#000",
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    textAlign: "center",
    lineHeight: 20,
  },
  myPositionCard: {
    marginBottom: 16,
    borderRadius: 16,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  myPositionGradient: {
    padding: 16,
  },
  myPositionContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  myPositionLeft: {
    flex: 1,
  },
  myPositionLabel: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "rgba(255, 255, 255, 0.8)",
    marginBottom: 4,
  },
  myPositionName: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFF",
  },
  myPositionRight: {
    alignItems: "flex-end",
  },
  myPositionRank: {
    fontFamily: "Inter_700Bold",
    fontSize: 24,
    color: "#FFF",
  },
  myPositionPoints: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "rgba(255, 255, 255, 0.9)",
  },
  statsContainer: {
    backgroundColor: "#FFF",
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  statsTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 14,
    color: "#000",
    marginBottom: 16,
    textAlign: "center",
  },
  statsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  statItem: {
    flex: 1,
    backgroundColor: "#F8F9FA",
    borderRadius: 12,
    padding: 12,
    alignItems: "center",
  },
  statItemFull: {
    flex: 1,
  },
  statValue: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#000",
    marginTop: 8,
  },
  statLabel: {
    fontFamily: "Inter_400Regular",
    fontSize: 11,
    color: "#666",
    textAlign: "center",
    marginTop: 2,
  },
});
